import { Component, OnInit, OnDestroy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { CommonService } from 'src/app/shareModule/services/common.service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-customization',
  templateUrl: './customization.component.html',
  styleUrls: ['./customization.component.scss']
})
export class CustomizationComponent implements OnInit, OnDestroy {
  // Default values for each field
  private readonly defaultHomepageColor = {
    type: 'gradient' as 'solid' | 'gradient',
    colors: ['#7318cd', '#9b1dff', '#6320ff', '#bc1aab'],
    stops: [0, 40, 70, 100]
  };

  homepageColor: {
    type: 'solid' | 'gradient';
    colors: string[];
    stops?: number[]; // Optional percentage stops for gradient
  } = {
    type: 'gradient',
    colors: ['#7318cd', '#9b1dff', '#6320ff', '#bc1aab'],
    stops: [0, 40, 70, 100]
  };

  isLoading: boolean = false;
  isSaving: boolean = false;
  private customizationSubscription: Subscription;

  constructor(
    public activeModal: NgbActiveModal,
    private commonService: CommonService,
    private toastr: ToastrService
  ) { }

  ngOnInit(): void {
    this.loadCustomization();
    // Subscribe to customization updates
    this.customizationSubscription = this.commonService.customizationObserver.subscribe(
      (customization: any) => {
        if (customization && customization.homepageColor) {
          try {
            const savedColor = typeof customization.homepageColor === 'string' 
              ? JSON.parse(customization.homepageColor) 
              : customization.homepageColor;
            
            if (savedColor && savedColor.type && savedColor.colors) {
              this.homepageColor = {
                type: savedColor.type,
                colors: savedColor.colors,
                stops: savedColor.stops || undefined
              };
            }
          } catch (e) {
            console.error('Error parsing homepageColor:', e);
          }
        }
      }
    );
  }

  ngOnDestroy(): void {
    if (this.customizationSubscription) {
      this.customizationSubscription.unsubscribe();
    }
  }

  loadCustomization(): void {
    this.isLoading = true;
    this.commonService.getCustomization();
    // The subscription will handle the data update
    setTimeout(() => {
      this.isLoading = false;
    }, 500);
  }

  closeModal(): void {
    this.activeModal.close();
  }

  setColorType(type: 'solid' | 'gradient'): void {
    this.homepageColor.type = type;
    // If switching to solid and we have multiple colors, keep only the first one
    if (type === 'solid' && this.homepageColor.colors.length > 1) {
      this.homepageColor.colors = [this.homepageColor.colors[0]];
    }
    // If switching to gradient and we only have one color, add a second one
    if (type === 'gradient' && this.homepageColor.colors.length === 1) {
      this.homepageColor.colors.push('#a29bfe');
    }
  }

  addColor(): void {
    if (this.homepageColor.type === 'gradient') {
      this.homepageColor.colors.push('#a29bfe');
    }
  }

  removeColor(index: number): void {
    if (this.homepageColor.colors.length > 1) {
      this.homepageColor.colors.splice(index, 1);
    }
  }

  updateColor(index: number, color: string): void {
    this.homepageColor.colors[index] = color;
  }

  getPreviewStyle(): string {
    if (this.homepageColor.type === 'solid') {
      return `background: ${this.homepageColor.colors[0]}`;
    } else {
      // Build gradient with percentages if available
      if (this.homepageColor.stops && this.homepageColor.stops.length === this.homepageColor.colors.length) {
        const gradientStops = this.homepageColor.colors.map((color, index) => 
          `${color} ${this.homepageColor.stops[index]}%`
        ).join(', ');
        return `background: linear-gradient(135deg, ${gradientStops})`;
      } else {
        // Fallback to simple gradient without percentages
        const gradientColors = this.homepageColor.colors.join(', ');
        return `background: linear-gradient(135deg, ${gradientColors})`;
      }
    }
  }

  resetHomepageColor(): void {
    this.homepageColor = {
      type: this.defaultHomepageColor.type,
      colors: [...this.defaultHomepageColor.colors],
      stops: [...this.defaultHomepageColor.stops]
    };
    this.toastr.info('Homepage Color reset to default');
  }

  isHomepageColorDefault(): boolean {
    return (
      this.homepageColor.type === this.defaultHomepageColor.type &&
      this.homepageColor.colors.length === this.defaultHomepageColor.colors.length &&
      this.homepageColor.colors.every((color, index) => color === this.defaultHomepageColor.colors[index]) &&
      (!this.homepageColor.stops || 
       (this.homepageColor.stops.length === this.defaultHomepageColor.stops.length &&
        this.homepageColor.stops.every((stop, index) => stop === this.defaultHomepageColor.stops[index])))
    );
  }

  saveCustomization(): void {
    this.isSaving = true;
    const payload = {
      homepageColor: this.homepageColor
    };

    this.commonService.saveCustomization(payload).subscribe({
      next: (response: any) => {
        this.isSaving = false;
        this.toastr.success('Customization saved successfully!');
        // Update the local customization object
        this.commonService.customization = {
          ...this.commonService.customization,
          homepageColor: this.homepageColor
        };
        this.commonService.customizationObserver.next(this.commonService.customization);
        this.closeModal();
      },
      error: (error: any) => {
        this.isSaving = false;
        console.error('Error saving customization:', error);
        this.toastr.error(error?.error?.message || 'Failed to save customization. Please try again.');
      }
    });
  }
}

